import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addBulkUsersToCustomer,
  addCustomerUserDocument,
  addNewCustomerUserNote,
  bulkDeleteCustomerUsers,
  bulkUpdateCustomerUsersManagerOrSite,
  createCustomerUser,
  createCustomerUserOnBoarding,
  customerUserSearch,
  deleteBulkCustomerUserDocuments,
  deleteBulkCustomerUserNotes,
  editCustomerUserDocumentDescription,
  getAllCustomerUsers,
  getAllCustomerUsersByMspId,
  getCustomerUserCategories,
  getCustomerUserDocuments,
  getCustomerUserManagers,
  getCustomerUserNotes,
  getUserDetails,
  removeCustomerUserProfilePhoto,
  resetCustomerUserPassword,
  setCustomerUserManagers,
  updateCustomerUser,
  updateCustomerUserCategory,
  updateCustomerUserNote,
  updateCustomerUserProfilePhoto,
  updateCustomerUserStatus,
} from "controller/api";
import {
  AddNewCustomerUserNoteInput,
  BulkCreateCustomerUserInput,
  BulkDeleteCustomerUserInput,
  CreateCustomerUserInput,
  CreateCustomerUserOnboarding,
  CustomerUserSearchInput,
  GetAllCustomerUsersByMspIdInput,
  GetAllCustomerUsersInput,
  GetCustomerUserDocumentsInput,
  GetQueryProps,
  SetCustomerUserInput,
  UpdateCustomerUserNote,
} from "shared";
import type { ApiErrorType } from "shared";
import { generateQueryKey } from "controller/cache/helpers/queryKeyGenerator";
import { sentryLogger } from "utilz";
import { debugConsole } from "utilz/helpers";

export const useCustomerUserSearch = (
  value: CustomerUserSearchInput & { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [
      "customerUserDropdown",
      value.searchQuery,
      value.siteId,
      value.customerId,
    ],
    queryFn: () => customerUserSearch(value),
    staleTime: 2000,
    enabled: value.enabled === false ? false : true,
  });
};

export const useGetAllCustomerUsers = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery = "",
  customerId,
}: GetAllCustomerUsersInput) => {
  return useQuery({
    queryKey: [
      "users",
      customerId,
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      filter,
      searchQuery,
    ],
    queryFn: () =>
      getAllCustomerUsers({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
        customerId,
      }),
    enabled: !!customerId,
    onError: (error) => console.log(error),
  });
};

export const useGetAllCustomerUsersByMutation = () => {
  return useMutation({
    mutationFn: (value: GetAllCustomerUsersInput) => getAllCustomerUsers(value),
  });
};

export const useGetAllCustomerUsersByMspId = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery = "",
  mspId,
  enabled = true,
}: GetAllCustomerUsersByMspIdInput & { enabled?: boolean }) => {
  return useQuery({
    queryKey: [
      "users_msp",
      mspId,
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      filter,
      searchQuery,
      perPageCount,
    ],
    queryFn: () =>
      getAllCustomerUsersByMspId({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
        mspId,
      }),
    enabled: !!mspId && enabled,
    onError: (error) => console.log(error),
  });
};

export const useGetUserDetails = (userId: string) => {
  return useQuery({
    queryKey: ["customer_user", userId],
    enabled: !!userId,
    queryFn: () => getUserDetails(userId),
    onError: (error) => {
      debugConsole(
        "🚀 ~ file: useUser.ts:10 ~ useGetUserCustomer ~ error:",
        error
      );
    },
  });
};

export const useGetCustomerUserManagers = (userId: string) => {
  return useQuery({
    queryKey: ["customer_user_managers", userId],
    queryFn: () => getCustomerUserManagers(userId),
    enabled: !!userId,
    onError(err) {
      debugConsole(
        "🚀 ~ file: useUser.ts:10 ~ useGetUserCustomer ~ error:",
        err
      );
    },
  });
};

export const useSetCustomerUserManagers = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: SetCustomerUserInput) => setCustomerUserManagers(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries([
          "customer_user_managers",
          // variables.userId,
        ]);
      },
      onError(error, variables, context) {
        debugConsole("useCustomerUser.ts 87 error: ", error);
      },
    }
  );
};

export const useBulkDeleteCustomerUsers = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: BulkDeleteCustomerUserInput) => bulkDeleteCustomerUsers(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["users_msp"]);
        queryClient.invalidateQueries(["users"]);
      },
      onError(error, variables, context) {
        // debugConsole();
      },
    }
  );
};

export const useCreateCustomerUser = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreateCustomerUserInput) => createCustomerUser(data),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["users_msp"]);
        // create ticket dropdown
        queryClient.invalidateQueries(["customerUserDropdown"]);
      },
      onError(error, variables, context) {},
    }
  );
};

export const useCreateCustomerUserOnBoarding = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreateCustomerUserOnboarding) => createCustomerUserOnBoarding(data),
    {
      onSuccess(data, variables, context) {
        setTimeout(() => {
          queryClient.invalidateQueries(["users"]);
          queryClient.invalidateQueries(["users_msp"]);
          // create ticket dropdown
          queryClient.invalidateQueries(["customerUserDropdown"]);
          queryClient.invalidateQueries(["msp"]); //
        }, 1000);
      },
      onError: (error: ApiErrorType) =>
        sentryLogger({
          error,
          workflow: "user",
          level: "error",
          functionName: "createCustomerUserOnBoarding",
        }),
    },
  );
};

export const useGetCustomerUserCategories = () => {
  return useQuery({
    queryKey: generateQueryKey({ entity: "customer-user-categories" }),
    queryFn: () => getCustomerUserCategories(),
    // MAU-2 is not live yet — fall back so the picker still works.
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "user",
        level: "error",
        functionName: "getCustomerUserCategories",
      }),
  });
};

/**
 * Rename a category. Invalidates the category list so every open picker — the
 * add-user drawer AND the user-profile drawer — repaints with the new label.
 */
export const useUpdateCustomerUserCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCustomerUserCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(
        generateQueryKey({ entity: "customer-user-categories" }),
      );
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "user",
        level: "error",
        functionName: "updateCustomerUserCategory",
      }),
  });
};

export const useUpdateCustomerUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCustomerUser,
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      queryClient.invalidateQueries(["customer_user"]);
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "user",
        level: "error",
        functionName: "updateCustomerUser",
      }),
  });
};

export const useUpdateCustomerUserStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCustomerUserStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      queryClient.invalidateQueries(["customer_user"]);
    },
  });
};

export const useUpdateCustomerUserProfilePhoto = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCustomerUserProfilePhoto,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([
        "customer_user",
        variables.customerUserId,
      ]);
    },
  });
};

export const useRemoveCustomerUserProfilePhoto = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeCustomerUserProfilePhoto,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([
        "customer_user",
        variables.customerUserId,
      ]);
    },
  });
};

export const useAddBulkUsersToCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: BulkCreateCustomerUserInput) => addBulkUsersToCustomer(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["msp"]);
      },
      onError(error, variables, context) {},
    }
  );
};

// NOTES
type GetQueryPropsWithCustomerUserId = GetQueryProps & {
  customerUserId: string;
};
export const useGetCustomerUserNotes = ({
  customerUserId,
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryPropsWithCustomerUserId) => {
  return useQuery({
    queryKey: ["customer_user_notes", customerUserId],
    queryFn: () =>
      getCustomerUserNotes({
        customerUserId,
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
  });
};

export const useAddCustomerUserNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerUserId, noteData }: AddNewCustomerUserNoteInput) =>
      addNewCustomerUserNote({ customerUserId, noteData }),
    onSuccess: () => queryClient.invalidateQueries(["customer_user_notes"]),
  });
};

export const useUpdateCustomerUserNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      noteId,
      customerUserId,
      noteData,
    }: UpdateCustomerUserNote) =>
      updateCustomerUserNote({ noteId, customerUserId, noteData }),
    onSuccess: () => queryClient.invalidateQueries(["customer_user_notes"]),
  });
};

export const useDeleteBulkCustomerUserNotes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteIds: string[]) => deleteBulkCustomerUserNotes({ noteIds }),
    onSuccess: () => queryClient.invalidateQueries(["customer_user_notes"]),
    onError: (err) => {},
  });
};

export const useGetCustomerUserDocuments = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  customerUserId,
}: GetCustomerUserDocumentsInput) => {
  return useQuery({
    queryKey: ["customer_user_documents", customerUserId],
    queryFn: () =>
      getCustomerUserDocuments({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
        customerUserId,
      }),
  });
};

export const useAddCustomerUserDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerUserId,
      formData,
    }: {
      customerUserId: string;
      formData: any;
    }) => addCustomerUserDocument({ customerUserId, formData }),
    onSuccess: () => {
      queryClient.invalidateQueries(["customer_user_documents"]);
    },
  });
};

export const useEditCustomerUserDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      attachment_id,
      data,
    }: {
      attachment_id: string;
      data: any;
    }) => editCustomerUserDocumentDescription({ attachment_id, data }),
    onSuccess: () => queryClient.invalidateQueries(["customer_user_documents"]),
  });
};

export const useDeleteBulkCustomerUserDocuments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { attachmentIds: string[] }) =>
      deleteBulkCustomerUserDocuments(data),
    onSuccess: () => queryClient.invalidateQueries(["customer_user_documents"]),
  });
};

export const useResetCustomerUserPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetCustomerUserPassword,
  });
};

export const useBulkUpdateCustomerUsersManagerOrSite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      typeId,
      userIds,
    }: {
      type: "manager" | "site";
      typeId: string;
      userIds: string[];
    }) => bulkUpdateCustomerUsersManagerOrSite({ type, typeId, userIds }),
    onSuccess: () => {
      queryClient.invalidateQueries(["users_msp"]);
      queryClient.invalidateQueries(["users"]);
      queryClient.invalidateQueries(["customer_user"]);
    },
  });
};
